import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { ToastContainer, type ToastItem, type ToastTone } from './Toast'

interface ToastContextValue {
  showToast: (toast: { tone: ToastTone; title: string; message?: string }) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

// crypto.randomUUID() throws outside secure contexts (plain HTTP on a
// non-localhost host), which would silently swallow the toast. A simple
// counter has no such restriction.
let nextToastId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback((toast: { tone: ToastTone; title: string; message?: string }) => {
    const id = String(++nextToastId)
    setToasts((prev) => [...prev, { id, ...toast }])
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx.showToast
}
